import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { MiniChartWidget } from 'components/dashboard/Chart';
import Button from 'components/form/Button';
import { NumberInput } from 'components/form/NumberInput';
import SelectInput from 'components/form/SelectInput';
import BackButton from 'components/layouts/BackButton';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import ConfirmationModal from './Modal';
import { fetchWallets } from 'api/wallet';
import { toast } from 'react-toastify';
import useWalletStore from 'state/wallet';
import useUserStore from 'state/userStore';

const plans = [
  {
    max: '$4,999',
    min: '$150',
    duration: '24 hrs',
    name: 'Starter',
    profit: 10,
  },
  {
    max: '$10,000',
    min: '$5,000',
    duration: '24 hrs',
    name: 'Premium',
    profit: 25,
  },
  {
    max: '$30,000',
    min: '$10,000',
    duration: '24 hrs',
    name: 'Gold',
    profit: 50,
  },
  {
    max: '50,000+',
    min: '$30,000',
    duration: '24 hrs',
    name: 'Platinum',
    profit: 65,
  },
];
// const plans = [
//   {
//     max: '$4,999',
//     min: '$150',
//     duration: '24 hrs',
//     name: 'Starter',
//     profit: 10,
//   },
//   {
//     max: '$19,999',
//     min: '$5,000',
//     duration: '24 hrs',
//     name: 'Premium',
//     profit: 25,
//   },
//   {
//     max: '$49,000',
//     min: '$20,000',
//     duration: '24 hrs',
//     name: 'Gold',
//     profit: 50,
//   },
//   {
//     max: 'unlimited',
//     min: '$50,000',
//     duration: '24 hrs',
//     name: 'Platinum',
//     profit: 65,
//   },
// ];

interface PlanContainerProps {
  plan: {
    duration: string;
    profit: number;
    max: string;
    min: string;
    name: string;
  };
  index: number;
  selectedPlan: number;
  handleChange: (index: number) => void;
}

const PlanContainer = ({
  plan,
  index,
  selectedPlan,
  handleChange,
}: PlanContainerProps) => {
  const { name, profit, min, max, duration } = plan;
  const selected = index === selectedPlan;
  return (
    <div
      className={clsx(
        selected && 'bg-indigo-500 text-white',
        'rounded-2xl border border-zinc-200 p-8'
      )}
    >
      <label className="flex cursor-pointer flex-col items-center gap-y-5 md:flex-row">
        <div className="flex items-center gap-x-4 md:basis-[55%]">
          <input
            type="radio"
            name="check"
            id=""
            checked={selected}
            className="h-5 w-5 rounded-full"
            onChange={() => handleChange(index)}
          />
          <div>
            <p>{name}</p>
            <small className="mt-1 w-[90px] rounded-full bg-indigo-200 px-2 py-1 text-black">
              profit {profit}%
            </small>
          </div>
        </div>
        <div>
          <p>
            <span className="text-xl">
              {min} - {max}
            </span>{' '}
            / <span>{duration}</span>
          </p>
        </div>
      </label>
    </div>
  );
};

export const DepositSchema = z.object({
  coin: z.string(),
  amount: z.string().min(3, 'amount is too small'),
});

export type DepositSchemaType = z.infer<typeof DepositSchema>;

const Investment = () => {
  window.document.title = 'Invest';

  const { setData, data } = useWalletStore();
  const { user } = useUserStore();

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<DepositSchemaType>({
    resolver: zodResolver(DepositSchema),
    mode: 'onTouched',
    defaultValues: {
      coin: 'bitcoin',
    },
  });
  const [cryptoIcon, setCryptoIcon] = useState('/assets/icons/bitcoin_two.svg');
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [open, setOpen] = useState(false);

  const handleIconChange = (value: string) => {
    switch (value) {
      case 'bitcoin':
        setCryptoIcon('/assets/icons/bitcoin_two.svg');
        break;
      case 'usdt trc-20':
      case 'usdt erc-20':
        setCryptoIcon('/assets/icons/usdt.svg');
        break;
      case 'ethereum':
        setCryptoIcon('/assets/icons/ethereum.svg');
        break;
      case 'bnb':
        setCryptoIcon('/assets/icons/bnb.svg');
        break;
      default:
        setCryptoIcon('/assets/icons/bitcoin_two.svg');
        break;
    }
  };

  const handleChange = (index: number) => {
    setSelectedPlan(index);
  };

  const toggleModal = (clear = false) => {
    setOpen(!open);

    // if (clear) {
    //   reset();
    //   setCryptoIcon('/assets/icons/bitcoin_two.svg');
    // }
  };

  const amount = useWatch({ control, name: 'amount' });
  const coin = useWatch({ control, name: 'coin' });

  const onSubmit = (data: DepositSchemaType) => {
    toggleModal();
  };

  const getWallets = async () => {
    try {
      const wallets = await fetchWallets(user?.exchange);
      setData(wallets.data);
    } catch (error) {
      console.log(error);
      toast.error('something went wrong');
    }
  };

  useEffect(() => {
    getWallets();
  }, []);

  return (
    <section className="py-5">
      <BackButton text="investment" />
      <div className="py-3">
        <div className="mb-5 flex flex-col items-center justify-between gap-10 lg:mb-0 lg:flex-row">
          <div className="w-full py-8 lg:basis-[50%]">
            <h4 className="text-lg">Investment Plans</h4>
            <br />
            <div className="flex flex-col gap-y-4">
              {plans.map((item, index) => (
                <PlanContainer
                  plan={{ ...item }}
                  key={index}
                  index={index}
                  selectedPlan={selectedPlan}
                  handleChange={handleChange}
                />
              ))}
            </div>
          </div>
          <div className="w-full md:basis-[45%]">
            <br />
            <div className="rounded-2xl border bg-gray-100 px-10 py-5">
              <form className="my-10 flex flex-col gap-y-8">
                <div className="flex flex-col gap-y-2">
                  <label htmlFor="coin" className="font-semibold">
                    Coin:
                  </label>

                  <SelectInput
                    control={control}
                    options={data.map((item) => ({ label: item.name }))}
                    name="coin"
                    containerClass="bg-white !py-3 md:!py-1"
                    startIcon={
                      <img
                        src={cryptoIcon}
                        alt="coin_icon"
                        className="h-6 w-6"
                      />
                    }
                    handleOnChange={handleIconChange}
                  />
                </div>
                <div className="flex flex-col gap-y-2">
                  <label htmlFor="coin" className="font-semibold">
                    Amount:
                  </label>

                  <NumberInput
                    control={control}
                    name="amount"
                    placeholder="$12,000"
                    prefix={'$'}
                    decimalScale={0}
                    fixedDecimalScale
                    className="py-3 md:py-1"
                  />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-lg font-semibold text-gray-500">Total:</p>
                  <p>{useWatch({ control, name: 'amount' }) ?? '$0'}</p>
                </div>
                <Button
                  className="flex w-full items-center justify-center gap-x-3 rounded-md bg-brand-dark !py-2 text-white"
                  disabled={!isValid || !isDirty}
                  onClick={handleSubmit(onSubmit)}
                >
                  proceed
                </Button>
              </form>
              <div className="flex justify-center gap-x-2">
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                </span>
                <p>secured</p>
              </div>
            </div>
          </div>
        </div>
        <MiniChartWidget />
      </div>
      {open && (
        <ConfirmationModal
          toggleModal={toggleModal}
          plan={plans[selectedPlan]}
          amount={amount}
          currency={coin}
        />
      )}
    </section>
  );
};

export default Investment;
